import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import { AUTH_URL } from '../contexts/AuthContextMySQL';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [tokenValid, setTokenValid] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    fetch(`${AUTH_URL}/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(res => res.json())
      .then(data => setTokenValid(!!data.valid))
      .catch(() => setTokenValid(false));
  }, [token]);

  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => navigate('/login'), 3000);
    return () => clearTimeout(timer);
  }, [done, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError('Le mot de passe doit contenir au moins 6 caractères');
    if (password !== confirmPassword) return setError('Les mots de passe ne correspondent pas');

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${AUTH_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Une erreur est survenue');
      setDone(true);
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'Serveur injoignable, réessayez plus tard' : err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-gray-950 border border-gray-700 rounded-lg py-3 pl-10 pr-12 text-white placeholder-gray-500 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-all";

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-4 py-8">
      <motion.div 
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src="/logo512.png" alt="AV Kamas" className="w-12 h-12 rounded-full shadow-[0_0_20px_rgba(250,204,21,0.4)]" />
            <span className="text-3xl font-bold tracking-wider">
              <span className="text-yellow-400">AV</span>
              <span className="text-white">KAMAS</span>
            </span>
          </Link>
        </div>

        <div className="bg-gray-900/80 backdrop-blur-md border border-gray-800 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-center mb-6">Nouveau mot de passe</h2>

          {tokenValid === null && (
            <p className="text-gray-400 text-center">Vérification du lien...</p>
          )}

          {tokenValid === false && (
            <div className="text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-gray-300 mb-6">Ce lien est invalide ou a expiré.</p>
              <Link to="/forgot-password" className="text-yellow-400 hover:text-yellow-300 font-semibold">
                Faire une nouvelle demande
              </Link>
            </div>
          )}

          {tokenValid && done && (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <p className="text-gray-300 mb-2">Mot de passe mis à jour !</p>
              <p className="text-gray-500 text-sm mb-6">Redirection vers la connexion...</p>
              <Link to="/login" className="text-yellow-400 hover:text-yellow-300 font-semibold">
                Se connecter
              </Link>
            </div>
          )}

          {tokenValid && !done && (
            <>
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nouveau mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      required
                      className={inputClass}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirmer le mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      required
                      className={inputClass}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  {loading ? 'Enregistrement...' : <>Changer le mot de passe <ArrowRight size={18} /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
